import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const empty = {
  hours_trained: "", plan_completion: "", difficulties: "", facilities: "",
  concentration: "", motivation: "", training_performance: "", match_performance: "",
  to_improve: "", next_goal: "", notes: "",
};

const steps = [
  { title: "Seu treino na semana", description: "Conte como foi sua rotina e o andamento do plano." },
  { title: "Concentração e rendimento", description: "Avalie sua motivação e seu desempenho nos treinos e partidas." },
  { title: "Próximos passos", description: "Registre o que deseja melhorar e sua meta para a próxima semana." },
];

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

export default function Feedback() {
  const [form, setForm] = useState(empty);
  const [history, setHistory] = useState([]);
  const [step, setStep] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [weeklyStatus, setWeeklyStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [historyError, setHistoryError] = useState("");
  const [statusError, setStatusError] = useState("");
  const reduceMotion = useReducedMotion();
  const entrance = reduceMotion ? {} : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, ease: "easeOut" } };
  useEffect(() => {
    if (!showThankYou) return undefined;
    const timeout = window.setTimeout(() => setShowThankYou(false), reduceMotion ? 1200 : 2400);
    return () => window.clearTimeout(timeout);
  }, [showThankYou, reduceMotion]);
  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(current => ({ ...current, [k]: undefined }));
  };
  const loadHistory = async ({ notify = true } = {}) => {
    try {
      const feedbackResponse = await api.get("/feedback");
      setHistory(feedbackResponse.data);
      // O histórico também deve aparecer quando ainda não há feedback da
      // semana atual. O status semanal controla apenas o questionário.
      setShowHistory(true);
      setHistoryError("");
      return feedbackResponse.data;
    } catch (err) {
      const message = err.response?.data?.detail || "Não foi possível carregar o histórico de feedbacks.";
      setHistoryError(message);
      if (notify) toast.error(message);
      throw err;
    }
  };

  const loadStatus = async ({ notify = true } = {}) => {
    try {
      const statusResponse = await api.get("/feedback/status");
      setWeeklyStatus(statusResponse.data);
      setStatusError("");
      return statusResponse.data;
    } catch (err) {
      const message = err.response?.data?.detail || "Não foi possível verificar a disponibilidade do feedback semanal.";
      setStatusError(message);
      if (notify) toast.error(message);
      throw err;
    }
  };

  const load = async ({ notify = true } = {}) => {
    await Promise.allSettled([
      loadHistory({ notify }),
      loadStatus({ notify }),
    ]);
  };
  useEffect(() => { load(); }, []);

  const validateStep = (targetStep) => {
    const fieldsByStep = [
      ["hours_trained", "plan_completion", "difficulties", "facilities"],
      ["concentration", "motivation", "training_performance", "match_performance"],
      ["to_improve", "next_goal", "notes"],
    ];
    const nextErrors = {};
    fieldsByStep[targetStep].forEach((field) => {
      const value = form[field];
      const invalidText = field !== "notes" && typeof value === "string" && !value.trim();
      const invalidHours = field === "hours_trained" && (value === "" || Number(value) < 0 || !Number.isFinite(Number(value)));
      const invalidScale = ["plan_completion", "concentration", "motivation", "training_performance", "match_performance"].includes(field)
        && ![1, 2, 3, 4, 5].includes(Number(value));
      if (invalidText || invalidHours || invalidScale) {
        nextErrors[field] = field === "hours_trained"
          ? "Informe as horas treinadas (zero também é válido)."
          : invalidScale ? "Escolha uma nota de 1 a 5." : "Preencha este campo obrigatório.";
      }
    });
    setErrors(current => ({ ...current, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const start = () => {
    if (!weeklyStatus || !weeklyStatus.available) return;
    setShowHistory(false);
    setErrors({});
    setStep(0);
  };

  const submit = async () => {
    if (submitting) return;
    if (![0, 1, 2].every(validateStep)) return;
    setSubmitting(true);
    try {
      const response = await api.post("/feedback", {
        ...form,
        hours_trained: Number(form.hours_trained),
        plan_completion: Number(form.plan_completion),
        concentration: Number(form.concentration),
        motivation: Number(form.motivation),
        training_performance: Number(form.training_performance),
        match_performance: Number(form.match_performance),
      });
      const submittedFeedback = response.data;
      setHistory(current => [submittedFeedback, ...current.filter(item => item.id !== submittedFeedback.id)]);
      setHistoryError("");
      setWeeklyStatus(current => ({
        ...(current || {}),
        available: false,
        submitted: true,
        submitted_at: submittedFeedback.created_at,
        week_start: submittedFeedback.week_start,
      }));
      setForm(empty);
      setStep(-1);
      setShowHistory(true);
      setShowThankYou(true);
      window.scrollTo(0, 0);
      toast.success("Feedback enviado!");
      const refresh = await Promise.allSettled([
        loadHistory({ notify: false }),
        loadStatus({ notify: false }),
      ]);
      if (refresh.some(result => result.status === "rejected")) {
        toast.error("Feedback salvo, mas não foi possível atualizar todas as informações da tela. Tente atualizar novamente.");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || "Você já enviou seu feedback desta semana.");
        await load();
        setStep(-1);
        setShowHistory(true);
      } else {
        toast.error(err.response?.data?.detail || "Erro ao enviar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout kind="athlete">
      <div className={`mx-auto flex w-full max-w-5xl flex-col ${step < 0 && !showHistory ? "min-h-[calc(100svh-11rem)] justify-center lg:min-h-[calc(100svh-5rem)]" : ""}`}>
        <header className="mx-auto w-full max-w-3xl text-center">
          <h1 className="text-3xl font-semibold text-white sm:text-4xl">Feedback Semanal GSX</h1>
          <p className="mt-2 text-slate-400">Sua percepção guia a evolução do seu plano.</p>
        </header>

        {step < 0 ? (
          <motion.section {...entrance} className="mx-auto mt-10 w-full max-w-3xl px-2 text-center sm:mt-12" aria-labelledby="feedback-intro-heading">
            <h2 id="feedback-intro-heading" className="text-xl font-semibold text-white sm:text-2xl">
              Responda aqui seu questionário de feedback semanal!
            </h2>
            <p className="mt-3 text-base font-medium text-slate-400 sm:text-lg">
              {weeklyStatus?.submitted ? "Feedback desta semana enviado! Consulte seu histórico abaixo." : "Clique abaixo para começar."}
            </p>
            {weeklyStatus && <p className="mt-2 text-sm text-slate-500">
              Semana de {formatDate(weeklyStatus.week_start)} a {formatDate(weeklyStatus.week_end)}. Um feedback por semana.
            </p>}
            {weeklyStatus?.submitted && <p className="mt-2 text-sm text-amber-300">Seu próximo feedback estará disponível no sábado, {formatDate(weeklyStatus.next_available)}.</p>}
            {statusError && <div role="alert" className="mx-auto mt-4 max-w-xl rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              <p>{statusError}</p>
              <Button type="button" variant="outline" onClick={() => loadStatus().catch(() => undefined)} className="mt-3 border-red-300/30 text-red-100 hover:bg-red-400/10">Tentar novamente</Button>
            </div>}
            <Button type="button" onClick={start} disabled={!weeklyStatus || weeklyStatus.submitted} data-testid="fb-start"
              className="mt-8 h-12 w-full max-w-sm bg-amber-500 text-base font-semibold text-[#071A33] hover:bg-amber-400">
              {weeklyStatus?.submitted ? "Feedback desta semana enviado" : weeklyStatus ? "Começar" : "Verificando disponibilidade..."}
            </Button>
          </motion.section>
        ) : (
          <motion.section {...entrance} className="mx-auto w-full max-w-4xl py-10 sm:py-12" aria-label="Questionário de feedback semanal">
            <form onSubmit={(event) => event.preventDefault()} className="space-y-8 px-1 sm:px-4" data-testid="feedback-form">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-amber-300">Etapa {step + 1} de {steps.length}</p>
                <h2 className="mt-3 text-2xl font-semibold text-white sm:text-3xl">{steps[step].title}</h2>
                <p className="mt-2 text-base text-slate-400">{steps[step].description}</p>
              </div>

              <div className="grid grid-cols-3 gap-2" role="progressbar" aria-label="Progresso do questionário"
                aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1}>
                {steps.map((item, index) => (
                  <span key={item.title} aria-hidden="true" className={`h-2 rounded-full transition-colors duration-300 motion-reduce:transition-none ${index <= step ? "bg-amber-400" : "bg-white/10"}`} />
                ))}
              </div>

              {Object.values(errors).filter(Boolean).length > 0 && (
                <div role="alert" className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {Object.values(errors).filter(Boolean).map((message, index) => <p key={`${message}-${index}`}>{message}</p>)}
                </div>
              )}

              <motion.div key={step} {...entrance} className="space-y-7">
                {step === 0 && <>
                  <NumberField label="Horas treinadas na semana" value={form.hours_trained} onChange={(v) => set("hours_trained", v)} testid="fb-hours" />
                  <ScaleField label="Cumprimento do plano" value={form.plan_completion} onChange={(v) => set("plan_completion", v)} testid="fb-plan" />
                  <TextField label="Principais dificuldades" value={form.difficulties} onChange={(v) => set("difficulties", v)} />
                  <TextField label="Principais facilidades" value={form.facilities} onChange={(v) => set("facilities", v)} />
                </>}
                {step === 1 && <>
                  <ScaleField label="Concentração" value={form.concentration} onChange={(v) => set("concentration", v)} testid="fb-concentration" />
                  <ScaleField label="Motivação" value={form.motivation} onChange={(v) => set("motivation", v)} testid="fb-motivation" />
                  <ScaleField label="Rendimento nos treinos" value={form.training_performance} onChange={(v) => set("training_performance", v)} testid="fb-training" />
                  <ScaleField label="Rendimento nas partidas" value={form.match_performance} onChange={(v) => set("match_performance", v)} testid="fb-match" />
                </>}
                {step === 2 && <>
                  <TextField label="O que precisa melhorar" value={form.to_improve} onChange={(v) => set("to_improve", v)} />
                  <TextField label="Meta para a próxima semana" value={form.next_goal} onChange={(v) => set("next_goal", v)} />
                  <TextField label="Observações adicionais" value={form.notes} onChange={(v) => set("notes", v)} area />
                </>}
              </motion.div>

              <div className="flex flex-col gap-3 border-t border-amber-400/15 pt-7 sm:flex-row sm:justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(current => current - 1)} disabled={step === 0} data-testid="fb-back"
                  className="h-12 border-amber-400/30 bg-transparent px-6 text-base text-slate-100 transition-transform motion-safe:hover:-translate-y-0.5 hover:bg-amber-400/10 hover:text-white">
                  Voltar
                </Button>
                {step < steps.length - 1 ? (
                  <Button type="button" onClick={() => { if (validateStep(step)) setStep(current => current + 1); }} data-testid="fb-next"
                    className="h-12 bg-amber-500 px-6 text-base font-semibold text-[#071A33] transition-transform motion-safe:hover:-translate-y-0.5 hover:bg-amber-400">
                    Próxima etapa
                  </Button>
                ) : (
                  <Button type="button" onClick={submit} disabled={submitting} data-testid="fb-submit"
                    className="h-12 w-full bg-amber-500 px-6 text-base font-semibold text-[#071A33] transition-transform motion-safe:hover:-translate-y-0.5 hover:bg-amber-400 sm:w-auto">
                    {submitting ? "Enviando..." : "Confirmar e enviar feedback"}
                  </Button>
                )}
              </div>
            </form>
          </motion.section>
        )}

        {(showHistory || historyError) && <motion.section {...entrance} className="mt-8 scroll-mt-8 border-t border-amber-400/10 pt-8 sm:mt-12" aria-labelledby="feedback-history-heading">
          <h2 id="feedback-history-heading" className="mb-5 text-xl font-semibold text-white">Histórico de feedbacks</h2>
          {historyError ? (
            <div role="alert" className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              <p>{historyError}</p>
              <Button type="button" variant="outline" onClick={() => loadHistory().catch(() => undefined)} className="mt-3 border-red-300/30 text-red-100 hover:bg-red-400/10">Tentar novamente</Button>
            </div>
          ) : history.length ? (
            <div className="grid gap-4 md:grid-cols-2">
              {history.map(h => (
                <Card key={h.id} className="bg-transparent gsx-card-dark p-5">
                  <div className="text-xs text-amber-300">{new Date(h.created_at).toLocaleDateString("pt-BR")}</div>
                  <div className="mt-2 text-sm text-slate-300">Horas: <span className="font-mono">{h.hours_trained}</span> • Plano: {h.plan_completion}/5</div>
                  <div className="mt-1 text-xs text-slate-400">Motivação {h.motivation}/5 • Concentração {h.concentration}/5</div>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Sem feedbacks ainda.</p>
          )}
        </motion.section>}
      </div>
      <AnimatePresence>
        {showThankYou && (
          <motion.div
            key="feedback-thank-you"
            role="status"
            aria-live="polite"
            initial={reduceMotion ? false : { opacity: 0, filter: "blur(8px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: "blur(12px)" }}
            transition={{ duration: reduceMotion ? 0 : 0.55, ease: "easeInOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#061426] px-6 text-center"
          >
            <div className="mx-auto max-w-xl">
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, scale: 0.75 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: reduceMotion ? 0 : 0.55, delay: reduceMotion ? 0 : 0.15, ease: "easeOut" }}
                className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-400 shadow-[0_0_55px_rgba(251,191,36,0.16)]"
              >
                <Check aria-hidden="true" className="h-10 w-10" strokeWidth={2.5} />
              </motion.div>
              <h2 className="mt-8 text-3xl font-semibold text-white sm:text-4xl">Obrigado pelo seu feedback!</h2>
              <p className="mt-3 text-base text-slate-300 sm:text-lg">Suas respostas ajudam a evoluir o seu plano.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-3">
      <Label className="text-base text-slate-300">{label} <span className="text-amber-300" aria-hidden="true">*</span></Label>
      <Input type="number" min={0} step={1} required value={value} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 border-amber-500/20 bg-[#0D2A52] px-4 text-base text-white md:text-base" />
    </div>
  );
}

function TextField({ label, value, onChange, area }) {
  return (
    <div className="space-y-3">
      <Label className="text-base text-slate-300">{label} {!area && <span className="text-amber-300" aria-hidden="true">*</span>}</Label>
      {area ? (
        <Textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)}
          className="min-h-32 border-amber-500/20 bg-[#0D2A52] px-4 py-3 text-base text-white" />
      ) : (
        <Input required value={value} onChange={(e) => onChange(e.target.value)}
          className="h-12 border-amber-500/20 bg-[#0D2A52] px-4 text-base text-white md:text-base" />
      )}
    </div>
  );
}

function ScaleField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between"><Label className="text-base text-slate-300">{label} <span className="text-amber-300" aria-hidden="true">*</span></Label><span className="font-mono text-base text-amber-300">{value || "—"}/5</span></div>
      <div className="flex gap-2 sm:gap-3">
        {[1,2,3,4,5].map(n => (
          <button type="button" key={n} onClick={() => onChange(n)}
            data-testid={testid ? `${testid}-${n}` : undefined}
            className={`h-12 flex-1 rounded-lg border text-base transition-colors ${
              Number(value) >= n
                ? "bg-amber-500 text-[#071A33] border-amber-500"
                : "border-amber-500/30 text-slate-300 hover:bg-amber-500/10"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
